<template>
  <!-- Below lg the sidebar is an off-canvas drawer; tap the backdrop to close. -->
  <div v-if="mobileOpen" class="fixed inset-0 z-40 bg-black/40 lg:hidden" @click="closeMobile"></div>
  <aside
    class="fixed inset-y-0 left-0 z-50 flex w-[248px] shrink-0 flex-col border-r border-line bg-surface transition-transform duration-200 lg:static lg:z-auto lg:translate-x-0 lg:transition-[width]"
    :class="[compact ? 'lg:w-[64px]' : 'lg:w-[248px]', mobileOpen ? 'translate-x-0' : '-translate-x-full']"
  >
    <!-- Brand -->
    <div class="flex h-14 items-center gap-2.5 px-4" :class="compact ? 'justify-center !px-0' : ''">
      <div class="relative size-7 shrink-0">
        <div class="absolute inset-0 rounded-md bg-brand-600"></div>
        <div class="absolute -bottom-0.5 -right-0.5 size-3 rounded-sm bg-mark ring-2 ring-surface"></div>
      </div>
      <span v-if="!compact" class="text-[15px] font-extrabold tracking-tight text-ink">Lymon</span>
    </div>

    <!-- Nav -->
    <nav class="flex-1 overflow-y-auto px-3 pb-3" :class="compact ? '!px-2' : ''">
      <template v-for="(section, si) in sections" :key="si">
        <p v-if="section.label && !compact" class="ov px-2.5 pb-1.5" :class="si === 0 ? 'pt-1' : 'pt-5'">
          {{ t(section.label) }}
        </p>
        <div v-else-if="section.label && compact" class="mx-1.5 my-2 border-t border-line"></div>
        <div v-else :class="si === 0 ? 'pt-1' : 'pt-3'"></div>
        <RouterLink v-for="item in section.items" :key="item.to" :to="item.to" :title="compact ? t(item.label) : undefined" :class="linkClass(isActive(item.to))">
          <component :is="item.icon" class="size-[18px] shrink-0" :stroke-width="isActive(item.to) ? 2.4 : 2" />
          <span v-if="!compact" class="truncate">{{ t(item.label) }}</span>
        </RouterLink>
      </template>

      <!-- Owner-only governance -->
      <template v-if="auth.isOwner">
        <p v-if="!compact" class="ov px-2.5 pb-1.5 pt-5">{{ t('layout.sections.owner') }}</p>
        <div v-else class="mx-1.5 my-2 border-t border-line"></div>
        <RouterLink v-for="item in ownerItems" :key="item.to" :to="item.to" :title="compact ? t(item.label) : undefined" :class="linkClass(isActive(item.to))">
          <component :is="item.icon" class="size-[18px] shrink-0" :stroke-width="isActive(item.to) ? 2.4 : 2" />
          <span v-if="!compact" class="truncate">{{ t(item.label) }}</span>
        </RouterLink>
      </template>

      <!-- Ops admin -->
      <template v-if="auth.isAdmin">
        <p v-if="!compact" class="ov px-2.5 pb-1.5 pt-5">{{ t('layout.sections.opsAdmin') }}</p>
        <div v-else class="mx-1.5 my-2 border-t border-line"></div>
        <RouterLink :to="'/ops'" :title="compact ? t('layout.crumbs.ops') : undefined" :class="linkClass(isActive('/ops'))">
          <Activity class="size-[18px] shrink-0" :stroke-width="isActive('/ops') ? 2.4 : 2" />
          <span v-if="!compact" class="truncate">{{ t('layout.crumbs.ops') }}</span>
        </RouterLink>
      </template>
    </nav>

    <!-- Footer -->
    <div v-if="!compact" class="flex items-center gap-1.5 border-t border-line px-4 py-3 text-xs text-faint">
      <span class="font-medium text-muted">Lymon</span>
      <span class="text-line-strong">·</span>
      <span>by Lybits</span>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { computed, markRaw, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import {
  Home, Rocket, MessageCircle, LayoutDashboard, Package,
  Database, Plug, Radio, Bell, Bot, Users, FolderTree, Settings2, CreditCard, Gauge, Sparkles,
  ScrollText, ShieldAlert, Activity, MonitorSmartphone, Hexagon, Siren, BookOpen, Workflow,
} from 'lucide-vue-next';
import { useAuthStore } from '../stores/auth';
import { useSidebar } from '../composables/useSidebar';
import { usePlatformFeatures } from '../composables/usePlatformFeatures';
import { useI18n } from 'vue-i18n';

const auth = useAuthStore();
const { projectsEnabled } = usePlatformFeatures();
const route = useRoute();
const { collapsed, mobileOpen, closeMobile } = useSidebar();
const { t } = useI18n();

// Collapse (icon-only rail) is a DESKTOP affordance; below lg the drawer always
// shows full labels. `compact` = collapsed AND on a large screen.
const isLg = ref(false);
let mql: MediaQueryList | null = null;
function syncLg(e: MediaQueryListEvent | MediaQueryList): void { isLg.value = e.matches; }
onMounted(() => {
  mql = window.matchMedia('(min-width: 1024px)');
  isLg.value = mql.matches;
  mql.addEventListener('change', syncLg);
});
onBeforeUnmount(() => mql?.removeEventListener('change', syncLg));
const compact = computed(() => collapsed.value && isLg.value);

// Navigating (tapping a link) closes the mobile drawer.
watch(() => route.fullPath, () => closeMobile());

interface NavItem { to: string; label: string; icon: unknown }
interface NavSection { label?: string; items: NavItem[] }

// Flat, labelled sections (everything visible — Linear/Vercel style).
// Labels are i18n keys resolved in the template (re-render on locale switch).
const sections = computed<NavSection[]>(() => [
  { items: [
    { to: '/', label: 'layout.crumbs.home', icon: markRaw(Home) },
    { to: '/quickstart', label: 'layout.crumbs.quickstart', icon: markRaw(Rocket) },
    { to: '/chat', label: 'layout.crumbs.chat', icon: markRaw(MessageCircle) },
  ] },
  { label: 'layout.sections.design', items: [
    ...(projectsEnabled.value ? [{ to: '/projects', label: 'projects.title', icon: markRaw(FolderTree) }] : []),
    { to: '/storyboard', label: 'layout.crumbs.overview', icon: markRaw(LayoutDashboard) },
    { to: '/components', label: 'layout.crumbs.components', icon: markRaw(Hexagon) },
    { to: '/widgets', label: 'layout.crumbs.widgetBundles', icon: markRaw(Package) },
    { to: '/screens', label: 'layout.crumbs.screens', icon: markRaw(MonitorSmartphone) },
  ] },
  { label: 'layout.sections.data', items: [
    { to: '/variables', label: 'layout.crumbs.variables', icon: markRaw(Database) },
    { to: '/sources', label: 'layout.crumbs.sources', icon: markRaw(Plug) },
    { to: '/agents', label: 'layout.crumbs.agents', icon: markRaw(Radio) },
  ] },
  { label: 'layout.sections.monitor', items: [
    { to: '/alerts', label: 'layout.crumbs.alerts', icon: markRaw(Bell) },
    { to: '/alarms', label: 'layout.crumbs.alarms', icon: markRaw(Siren) },
  ] },
  { label: 'layout.sections.workspace', items: [
    { to: '/llm-config', label: 'layout.crumbs.llmConfig', icon: markRaw(Bot) },
    { to: '/billing', label: 'layout.crumbs.billing', icon: markRaw(CreditCard) },
    { to: '/members', label: 'layout.crumbs.members', icon: markRaw(Users) },
    { to: '/organization', label: 'layout.crumbs.organization', icon: markRaw(FolderTree) },
    { to: '/settings', label: 'layout.crumbs.settings', icon: markRaw(Settings2) },
  ] },
]);

const ownerItems: NavItem[] = [
  { to: '/usage', label: 'layout.crumbs.usage', icon: markRaw(Gauge) },
  { to: '/copilot', label: 'layout.crumbs.copilot', icon: markRaw(Sparkles) },
  { to: '/automations', label: 'layout.crumbs.automations', icon: markRaw(Workflow) },
  { to: '/knowledge', label: 'layout.crumbs.knowledge', icon: markRaw(BookOpen) },
  { to: '/audit', label: 'layout.crumbs.audit', icon: markRaw(ScrollText) },
  { to: '/denials', label: 'layout.crumbs.denials', icon: markRaw(ShieldAlert) },
];

// Home only on exact "/"; "Overview" stays lit across the designer editors.
function isActive(to: string): boolean {
  const p = route.path;
  if (to === '/') return p === '/';
  if (to === '/projects' && p.startsWith('/design/') && route.query.project) return true;
  if (to === '/storyboard') return p === '/storyboard' || (!route.query.project && (p.startsWith('/design') || p.startsWith('/designer')));
  return p === to || p.startsWith(to + '/');
}

function linkClass(active: boolean): string[] {
  return [
    'group flex items-center rounded-lg py-2 text-sm transition-colors',
    compact.value ? 'justify-center px-0' : 'gap-2.5 px-2.5',
    active
      ? 'bg-brand-50 font-medium text-brand-700'
      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
  ];
}
</script>
